/**
 * The voice's switch and its size — everything about the narrator a price needs.
 *
 * The narrator itself lives in `ui/` (it writes into the live conversation),
 * and the engine imports nothing from `ui/`. But since D295 every figure
 * printed before a press includes the voice call that press will buy, and those
 * figures are the engine's (`projectVerbUsd`, `projectRunPressUsd`). So the two
 * facts the arithmetic reads — whether the voice is on, and how large one call
 * can be — live here, below both, and `ui/narrator.ts` reads them from here too:
 * one switch and one set of caps, so the quote and the call cannot disagree.
 *
 * No React and no store: one boolean in its own storage key, and three numbers.
 */

/* ── The off switch ─────────────────────────────────────────────────────── */

/**
 * Its own key, not a store field: the store's contract is deliberately hard to
 * touch, and this is one boolean — the same argument `core/conversations.ts`
 * makes for its shelf. Absent means ON: the voice is the experience, and the
 * switch exists for the reader who has priced it and wants the quiet.
 */
const VOICE_KEY = 'nv.voice'
const voiceWatchers = new Set<() => void>()
/** Carries the choice when the origin refuses writes (a private window): the
 *  press still applies for this tab, it just does not survive it. */
let voiceFallback: boolean | null = null

export function voiceOn(): boolean {
  try {
    return localStorage.getItem(VOICE_KEY) !== 'off'
  } catch {
    return voiceFallback ?? true
  }
}

export function setVoiceOn(on: boolean): void {
  voiceFallback = on
  try {
    if (on) localStorage.removeItem(VOICE_KEY)
    else localStorage.setItem(VOICE_KEY, 'off')
  } catch {
    /* the fallback above keeps the press honest for this tab */
  }
  for (const fn of voiceWatchers) fn()
}

/** Hears the switch move — for a surface whose printed figure includes the
 *  voice, as well as for the switch's own label. */
export function onVoiceChange(fn: () => void): () => void {
  voiceWatchers.add(fn)
  return () => voiceWatchers.delete(fn)
}

/* ── How large one call can be ──────────────────────────────────────────── */

/** What a narration follows: one pass a person pressed, or a whole run. */
export type VoiceShape = 'operation' | 'run'

/**
 * The output ceiling per shape, with room above the prose it asks for: a
 * ceiling the asked-for shape barely fits under turns a wrap-up that runs a
 * little long into a sentence that stops. The room once held the trailer the
 * voice no longer writes (D295), and now holds the marks after its sentences.
 * Every figure printed before a press prices the output at exactly this cap.
 */
export const VOICE_MAX_TOKENS: Record<VoiceShape, number> = {
  operation: 500,
  run: 900,
}

/**
 * The input one narration is quoted at before the press, in tokens: the figure
 * `projectMoveUsd` quotes a move's input at, and a typical figure leaning high
 * in the same way, not a bound. A narration sends the research contract its
 * pass was sent, with the id table and the record where the pass sends its
 * brief, so the two are the same order of size.
 *
 * Measured rather than borrowed. `ui/narrator.test.ts` builds the largest
 * ORDINARY request — every part a cap bounds at its cap (the id table, the
 * record digest, the most nodes a pass may land) and the one part nothing caps,
 * the premise chain the contract carries whole, eight deep with every link
 * written to the longest the passes' own rules allow — and holds it under this
 * at three bytes a token. Prose runs nearer four, and a CJK ideograph is three
 * bytes and about one token.
 *
 * What it costs: a chain deeper than that, or links longer than the rules ask,
 * carries a request past the allowance, and the printed figure then leans low by
 * the difference. A run's ceiling does not rest on it: the summary is checked
 * against the ceiling from the request actually about to be sent, a token a
 * byte (`voiceBoundUsd`), which no tokenizer undercuts.
 */
export const VOICE_INPUT_TOKENS = 12_000

/**
 * Provider framing on top of a request's own text — role markers, message
 * scaffolding. An allowance rather than a measurement: framing adds a handful
 * of tokens per message, this is two orders above that, and the byte count it
 * is added to already over-counts ordinary prose several times over.
 */
export const VOICE_FRAMING_TOKENS = 1_000
